import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink, Routes, Route, Navigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { useTheme } from "@/context/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Building2, Users2, KeyRound, LayoutDashboard, ScrollText, Server,
  Sun, Moon, LogOut, Ban, ShieldCheck, Eye, Database, Webhook,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin", label: "Dashboard globale", icon: LayoutDashboard, end: true },
  { to: "/admin/tenants", label: "Aziende", icon: Building2 },
  { to: "/admin/logs", label: "Log sistema", icon: ScrollText },
  { to: "/admin/services", label: "Stato servizi", icon: Server },
];

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="hidden w-64 flex-col border-r border-border bg-card lg:flex">
        <div className="flex h-16 items-center border-b border-border px-5"><Logo /></div>
        <nav className="flex-1 space-y-0.5 px-3 py-4">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors", isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground")}>
              <n.icon className="h-[18px] w-[18px]" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <Badge variant="secondary" className="mb-2 w-full justify-center">Super Admin</Badge>
          <Button variant="ghost" className="w-full justify-start text-destructive" onClick={logout}><LogOut className="mr-2 h-4 w-4" /> Esci</Button>
        </div>
      </aside>
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between border-b border-border px-6">
          <div className="lg:hidden"><Logo /></div>
          <p className="hidden font-heading text-lg font-semibold lg:block">Console Piattaforma</p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} className="rounded-full">{theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</Button>
            <span className="text-sm text-muted-foreground">{user?.email}</span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <Routes>
            <Route index element={<GlobalDashboard />} />
            <Route path="tenants" element={<Tenants />} />
            <Route path="logs" element={<SystemLogs />} />
            <Route path="services" element={<ServicesStatus />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function GlobalDashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["sa-stats"], queryFn: async () => (await api.get("/superadmin/stats")).data.data });
  if (isLoading || !data) return <div className="grid grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>;
  const cards = [
    { label: "Aziende totali", value: data.tenants_total, icon: Building2 },
    { label: "Aziende attive", value: data.tenants_active, icon: ShieldCheck },
    { label: "Utenti totali", value: data.users_total, icon: Users2 },
    { label: "API key totali", value: data.apikeys_total, icon: KeyRound },
  ];
  return (
    <div>
      <h1 className="mb-6 font-heading text-2xl font-semibold sm:text-3xl">Statistiche globali</h1>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.label} data-testid={`sa-stat-${c.label}`}><CardContent className="p-5"><c.icon className="h-5 w-5 text-primary" /><p className="mt-3 font-heading text-2xl font-semibold">{c.value}</p><p className="text-xs text-muted-foreground">{c.label}</p></CardContent></Card>
        ))}
      </div>
      <Card className="mt-6">
        <CardHeader><CardTitle className="text-base">Abbonamenti per piano</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {Object.entries(data.subscriptions_by_plan || {}).map(([k, v]) => (
            <Badge key={k} variant="secondary" className="px-3 py-1.5 capitalize">{k}: {v}</Badge>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function Tenants() {
  const qc = useQueryClient();
  const { startImpersonation } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["sa-tenants"], queryFn: async () => (await api.get("/superadmin/tenants")).data.data });
  const suspend = async (id) => { try { await api.post(`/superadmin/tenants/${id}/suspend`); toast.success("Stato aggiornato"); qc.invalidateQueries({ queryKey: ["sa-tenants"] }); } catch (e) { toast.error(apiError(e)); } };
  const impersonate = async (t) => { try { await startImpersonation(t.id, t.name); toast.success(`Impersonificazione: ${t.name}`); window.location.href = "/dashboard"; } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <h1 className="mb-6 font-heading text-2xl font-semibold sm:text-3xl">Aziende</h1>
      <Card className="overflow-hidden" data-testid="sa-tenants-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Azienda</TableHead><TableHead>Piano</TableHead><TableHead>Utenti</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
            <TableBody>
              {isLoading ? <TableRow><TableCell colSpan={5}><Skeleton className="h-10 w-full" /></TableCell></TableRow> :
                data?.tenants.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell><p className="font-medium">{t.name}</p><p className="text-xs text-muted-foreground">{t.industry}</p></TableCell>
                    <TableCell><Badge variant="secondary" className="capitalize">{t.plan || "—"}</Badge></TableCell>
                    <TableCell>{t.users_count}</TableCell>
                    <TableCell><Badge variant="outline" className={t.status === "active" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-red-500/40 bg-red-500/10 text-red-600"}>{t.status === "active" ? "Attiva" : "Sospesa"}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => impersonate(t)} data-testid={`impersonate-${t.id}`}><Eye className="mr-1 h-3.5 w-3.5" /> Impersona</Button>
                        <Button variant="ghost" size="sm" onClick={() => suspend(t.id)} data-testid={`suspend-${t.id}`}><Ban className="mr-1 h-3.5 w-3.5" /> {t.status === "active" ? "Sospendi" : "Riattiva"}</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function SystemLogs() {
  const { data, isLoading } = useQuery({ queryKey: ["sa-logs"], queryFn: async () => (await api.get("/superadmin/system-logs")).data.data });
  return (
    <div>
      <h1 className="mb-6 font-heading text-2xl font-semibold sm:text-3xl">Log di sistema</h1>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Utente</TableHead><TableHead>Azione</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
            <TableBody>
              {isLoading ? <TableRow><TableCell colSpan={4}><Skeleton className="h-8 w-full" /></TableCell></TableRow> :
                data?.logs.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString("it-IT")}</TableCell>
                    <TableCell className="text-sm">{l.user_name}</TableCell>
                    <TableCell><code className="rounded bg-muted px-1.5 py-0.5 text-xs">{l.action}</code></TableCell>
                    <TableCell className="text-sm">{l.description}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function ServicesStatus() {
  const rows = [{ icon: Server, label: "Backend API" }, { icon: Database, label: "Database MongoDB" }, { icon: Webhook, label: "Webhook engine" }];
  return (
    <div>
      <h1 className="mb-6 font-heading text-2xl font-semibold sm:text-3xl">Stato servizi</h1>
      <div className="grid gap-4 sm:grid-cols-3">
        {rows.map((r) => (
          <Card key={r.label}><CardContent className="flex items-center justify-between p-5">
            <span className="flex items-center gap-2 text-sm font-medium"><r.icon className="h-5 w-5 text-muted-foreground" /> {r.label}</span>
            <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-600"><span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" /> Operativo</Badge>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}
